import { BrandLogo } from "../components/brand/BrandLogo";
import { RotatingPattern } from "../components/brand/RotatingPattern";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Anchor,
  Button,
  Card,
  Center,
  PasswordInput,
  Stack,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { authApi } from "../api/auth";
import { errorMessage } from "../lib/axios";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 6;
  const mismatch = confirm.length > 0 && password !== confirm;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (tooShort || mismatch || !token) return;
    setError(null);
    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      notifications.show({
        color: "green",
        message: "Password reset — please sign in.",
      });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Center mih="100vh" p="md" bg="var(--surface-canvas)" pos="relative" style={{ overflow: "hidden" }}>
      <RotatingPattern />
      <Card withBorder shadow="sm" radius="lg" padding="xl" pos="relative" style={{ zIndex: 1 }} w={400} maw="100%">
        <Stack gap="xs" mb="md">
          <BrandLogo height={40} />
          <Title order={4} fw={600}>
            Set a new password
          </Title>
        </Stack>

        {!token ? (
          <Stack>
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              This reset link is invalid or incomplete. Request a new one.
            </Alert>
            <Anchor component={Link} to="/forgot-password" size="sm">
              Request a reset link
            </Anchor>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                  {error}
                </Alert>
              )}
              <PasswordInput
                label="New password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                error={tooShort ? "At least 6 characters" : undefined}
                autoFocus
              />
              <PasswordInput
                label="Confirm new password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
                error={mismatch ? "Passwords don't match" : undefined}
              />
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!password || tooShort || mismatch}
              >
                Reset password
              </Button>
              <Anchor component={Link} to="/login" size="sm" ta="center">
                Back to sign in
              </Anchor>
            </Stack>
          </form>
        )}
      </Card>
    </Center>
  );
}
